import * as vp from 'vopay';
import * as Yup from 'yup';
import { FunctionComponent, useEffect, useState } from 'react';
import { Formik, FormikErrors, FormikTouched } from 'formik';
import styles from '../AccountingSystemOpen.module.scss';
import useFetchFinancialInstitutionBranches from '../../../../../hooks/useApi/useFetchFinancialInstitutionBranches';
import {
  buildPaymentUpdateRows,
  PaymentUpdateRows,
  getPaymentMethodsNeededDropdown,
} from './buildPaymentUpdateRows.helper';
import { PaymentUpdate } from '../types';
import { useClientData } from '../../../../../providers/ClientData.provider';
import ElinxConnect from '../../../../ElinxConnect/ElinxConnect';
import { getRequiredIcon } from '../utils';

type BankingUpdateDialogueProps = {
  isVisible: boolean;
  type: 'Payable' | 'Receivable';
  updateType: 'Edit' | 'Add';
  defaultValues: PaymentUpdate;
  originalValue: vp.TemporaryBillsOrInvoicesRails | undefined;
  financialInstitutions: {
    [key: string]: vp.FinancialInstitution;
  };
  close: () => void;
  onSave: (savedValues: PaymentUpdate) => void;
};
type FC = FunctionComponent<BankingUpdateDialogueProps>;

const BankingUpdateMiniDialogue: FC = ({
  isVisible,
  type,
  close,
  onSave,
  defaultValues,
  financialInstitutions,
  updateType,
  originalValue,
}: BankingUpdateDialogueProps) => {
  const {
    SubTitle,
    Box,
    Button,
    ElementContainer,
    TextField,
    Select,
    GoogleAddressField,
    Radio,
    DatepickerSimple,
    Icon,
  } = vp;

  const [currentValues, setCurrentValues] = useState(defaultValues);
  const [showElinxConnect, setShowElinxConnect] = useState(false);
  const {
    response: { Country },
  } = useClientData();
  const [financialInstitutionBranchesResponse, fetchFinancialInstitutionBranches] =
    useFetchFinancialInstitutionBranches({});
  const [elinxConnectSettings, setElinxConnectSettings] = useState<vp.EmbedUrlRequest>({
    RedirectURL: 'https://vopay.com',
    Version: 'v2',
    Country,
    PaymentSelectionMethod: 'credit',
    ClientControlled: false,
    RedirectMethod: 'JavascriptMessage',
  });

  const paymentUpdateRows = buildPaymentUpdateRows({
    type,
    updateType,
    country: Country,
    branchTransitNumbers: Object.values(financialInstitutionBranchesResponse.Branches || {}).map((value) => {
      const { BranchTransitNumber, Address, TownCity, PostalCode, Province } = value;
      return {
        label: `${BranchTransitNumber} ${Address}, ${TownCity}, ${PostalCode}, ${Province}`,
        value: BranchTransitNumber.toString(),
        renderOption: (
          <div>
            {' '}
            {BranchTransitNumber} <br /> {`${Address}, ${TownCity}, ${PostalCode}, ${Province}`}
          </div>
        ),
      };
    }),
    financialInstitutions: Object.values(financialInstitutions || {}).map((value) => ({
      label: `${value.FinancialInstitutionNumber} ${value.FIName}`,
      value: value.FinancialInstitutionNumber,
      renderOption: (
        <div>
          {value.FinancialInstitutionNumber} <br /> {value.FIName}
        </div>
      ),
    })),
    originalValue,
  });
  const paymentMethodOptions = Object.values(
    getPaymentMethodsNeededDropdown(
      originalValue || { hasBankAccount: false, hasCreditCard: false, hasDebitCard: false, hasInterac: false },
      Country,
      type,
    ),
  );
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const onSuccess = (eventValues: PaymentUpdate) => {
    setShowSaveSuccess(true);
    setTimeout(() => {
      setShowSaveSuccess(false);
      onSave(eventValues);
    }, 2000);
  };
  return (
    <Formik
      initialValues={
        updateType === 'Edit'
          ? defaultValues
          : {
              FinancialInstitutionNumber: '',
              BankTransitNumber: '',
              AccountNumber: '',
              RoutingNumber: '',
              Email: '',
              Phone: '',
              Question: '',
              Answer: '',
              InteracMethod: '',
              PaymentMethod: paymentMethodOptions.length === 1 ? paymentMethodOptions[0] : '',
              TokenDebit: '',
              TokenCredit: '',
              PhoneNumber: '',
              City: '',
              Address: '',
              State: '',
              ZipCode: '',
              IdentificationNumber: '',
            }
      }
      onSubmit={onSuccess}
      enableReinitialize
      validationSchema={Yup.object().shape(getValidationSchema(currentValues, paymentUpdateRows))}
      validateOnChange
      validateOnBlur
    >
      {({
        values,
        touched,
        errors,
        isValid,
        handleChange,
        handleBlur,
        handleSubmit,
        setValues,
        setTouched,
        resetForm,
        submitForm,
        validateForm,
      }) => {
        useEffect(() => {
          if (values.FinancialInstitutionNumber) {
            const FinancialInstitutionID = getFinancialInstitutionIDByNumber(values.FinancialInstitutionNumber);
            if (FinancialInstitutionID) {
              fetchFinancialInstitutionBranches({ FinancialInstitutionID: Number(FinancialInstitutionID) });
            }
          }
        }, [values.FinancialInstitutionNumber]);
        useEffect(() => {
          setCurrentValues({
            ...values,
          });
        }, [values]);
        useEffect(() => {
          void validateForm();
        }, [paymentUpdateRows]);
        return (
          <>
            <div className={[styles.dialogueMini, styles.dialogueSide, isVisible ? styles.show : ''].join(' ')}>
              <div className={styles.dialogueContainer}>
                <form className={[styles.dialogueBlock].join(' ')} onSubmit={handleSubmit}>
                  <div className={styles.dialogueSideHeader}>
                    <Box jc="center">
                      <SubTitle subTitle={`${updateType} Payment Method`} fontSize="20px" />
                    </Box>
                  </div>
                  <div className={styles.dialogueSideContainer}>
                    <Box pt="sm" pb="md" flx="col" ai="fs">
                      {paymentUpdateRows
                        .filter((element) => element.visible(values))
                        .map((element) =>
                          getFormElement(
                            element,
                            values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                            setValues,
                            setTouched,
                          ),
                        )}
                    </Box>
                  </div>
                  <div className={styles.dialogueSideFooter}>
                    {showSaveSuccess && (
                      <div className={styles.PaymentMethodSuccess}>
                        <div className={styles.PaymentMethodSuccessIcon}>
                          <Icon icon="check" height="10px" width="10px" color="primary-light" />
                        </div>{' '}
                        Payment Method {updateType === 'Add' ? 'Added' : 'Updated'}
                      </div>
                    )}

                    {!showSaveSuccess && (
                      <Box jc="center" gap="sm" flx="row" ai="center">
                        <Button
                          id="confirmationDialogue-close"
                          color="secondary"
                          label="Cancel"
                          height="36px"
                          width="100%"
                          onClick={() => {
                            resetForm();
                            close();
                          }}
                        />
                        {values.PaymentMethod !== 'Credit Card' && values.PaymentMethod !== 'Debit Card' && (
                          <Button
                            id="save-and-collect"
                            label="Save"
                            type="submit"
                            color="primary"
                            isDisabled={!isValid}
                            height="36px"
                            width="100%"
                          />
                        )}
                        {values.PaymentMethod === 'Credit Card' && (
                          <Button
                            id="save-and-collect"
                            label="Provide"
                            type="button"
                            color="primary"
                            height="36px"
                            width="100%"
                            onClick={() => {
                              setElinxConnectSettings({
                                RedirectURL: 'https://vopay.com',
                                Version: 'v2',
                                Country,
                                PaymentSelectionMethod: 'credit',
                                ClientControlled: false,
                                RedirectMethod: 'JavascriptMessage',
                              });
                              setShowElinxConnect(true);
                            }}
                          />
                        )}
                        {values.PaymentMethod === 'Debit Card' && (
                          <Button
                            id="save-and-collect"
                            label="Provide"
                            type="button"
                            color="primary"
                            height="36px"
                            width="100%"
                            onClick={() => {
                              setElinxConnectSettings({
                                RedirectURL: 'https://vopay.com',
                                Version: 'v2',
                                Country,
                                PaymentSelectionMethod: 'debitcard',
                                ClientControlled: false,
                                RedirectMethod: 'JavascriptMessage',
                              });
                              setShowElinxConnect(true);
                            }}
                          />
                        )}
                      </Box>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {showElinxConnect && (
              <ElinxConnect
                setting={elinxConnectSettings}
                onSuccess={(eventValues) => {
                  if (values.PaymentMethod === 'Debit Card') {
                    setValues({ ...values, TokenDebit: eventValues.data.Token });
                  } else {
                    setValues({ ...values, TokenCredit: eventValues.data.Token });
                  }

                  void submitForm();
                  setShowElinxConnect(false);
                }}
                onClose={() => {
                  setShowElinxConnect(false);
                }}
              />
            )}
          </>
        );
      }}
    </Formik>
  );
  function getFormElement(
    element: PaymentUpdateRows,
    values: PaymentUpdate,
    errors: FormikErrors<PaymentUpdate>,
    touched: FormikTouched<PaymentUpdate>,
    handleChange: (event: React.ChangeEvent<any>) => void,
    handleBlur: (event: React.FocusEvent<any>) => void,
    setValues: (values: React.SetStateAction<PaymentUpdate>, shouldValidate?: boolean | undefined) => void,
    setTouched: (touched: FormikTouched<PaymentUpdate>, shouldValidate?: boolean | undefined) => void,
  ) {
    const {
      type: elementType,
      id,
      preset,
      placeholder,
      label,
      showValue,
      require: required,
      getSelectOptions,
      getRadioOptions,
      container,
      paymentRows,
      isDisabled,
      renderOption,
      filterable,
    } = element;
    if (container === 'wrapper' || id === undefined) {
      return (
        <Box gap="sm" ai="fs" width="100%">
          {paymentRows
            .filter((subElement) => subElement.visible(values))
            .map((subElement) =>
              getFormElement(subElement, values, errors, touched, handleChange, handleBlur, setValues, setTouched),
            )}
        </Box>
      );
    }
    const isRequired = required(values);

    switch (elementType) {
      case 'input':
        return (
          <ElementContainer
            width="100%"
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            isBold={false}
            input={
              <TextField
                variant="outlined"
                preset={preset}
                name={id}
                placeholder={placeholder}
                onBlur={handleBlur}
                onChange={handleChange}
                width="100%"
                height="37px"
                disabled={isDisabled}
                value={String(values[id] || '')}
                error={errors[id] && touched[id] ? errors[id]?.toString() : null}
              />
            }
          />
        );
      case 'select':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            width="100%"
            justifyContent="flex-start"
            isBold={false}
            input={
              <Select
                version="2"
                style={{
                  '--triggerFontSize': '14px',
                  '--triggerPaddingInline': '12px',
                  '--triggerPaddingBlock': '6px',
                  '--triggerBorderRadius': '4px',
                  '--dropdownMaxWidth': 'auto',
                }}
                renderOption={renderOption}
                options={getSelectOptions ? getSelectOptions(values) : []}
                placeholder={placeholder || ''}
                name={id}
                onBlur={handleBlur}
                onChange={handleChange}
                renderSelectedValue={(value, { Placeholder }) => (
                  <div className={styles.dropDownValue}>
                    {showValue
                      ? value?.value || <Placeholder />
                      : value?.renderOption || value?.label || <Placeholder />}
                  </div>
                )}
                filterable={filterable}
                value={String(values[id] || '')}
                sort={false}
                errorMsg={errors[id] && touched[id] ? errors[id]?.toString() : undefined}
              />
            }
          />
        );
      case 'googleAddress':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            width="100%"
            justifyContent="flex-start"
            isBold={false}
            input={
              <GoogleAddressField
                variant="outlined"
                ISOCountry={['US']}
                preset={preset}
                placeholder=""
                name={id}
                onBlur={handleBlur}
                onChange={handleChange}
                value={String(values[id] || '')}
                error={errors[id] && touched[id] ? errors[id]?.toString() : null}
                onGoogleAddressSelect={(addressComponents: vp.AddressComponents) => {
                  handleChange(vp.createSyntheticEvent('Address', addressComponents.address));
                  handleChange(vp.createSyntheticEvent('City', addressComponents.city));
                  handleChange(vp.createSyntheticEvent('State', addressComponents.province));
                  handleChange(vp.createSyntheticEvent('ZipCode', addressComponents.postalcode));
                }}
              />
            }
          />
        );
      case 'radio':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            width="100%"
            isBold={false}
            justifyContent="flex-start"
            input={
              <Radio
                key={id}
                name={id}
                value={String(values[id])}
                options={getRadioOptions ? getRadioOptions(values) : {}}
                noOrdering
                onBlur={() => {}}
                onChange={handleChange}
                varient="inline"
                setCheckedValue={(value) => {
                  setValues({ ...values, Language: value as 'en' | 'fr' });
                }}
              />
            }
          />
        );

      case 'selectFinanacialInstitution':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            width="100%"
            isBold={false}
            justifyContent="flex-start"
            input={
              <Select
                version="2"
                options={getSelectOptions ? getSelectOptions(values) : []}
                placeholder={placeholder || ''}
                name={id}
                renderSelectedValue={(value, { Placeholder }) => (
                  <div className={styles.dropDownValue}>
                    {showValue
                      ? value?.value || <Placeholder />
                      : value?.renderOption || value?.label || <Placeholder />}
                  </div>
                )}
                filterable={filterable}
                renderOption={renderOption}
                onBlur={handleBlur}
                onChange={(e) => {
                  setValues({ ...values, BankTransitNumber: '' });
                  setTouched({ ...touched, BankTransitNumber: false });
                  handleChange(e);
                }}
                style={{
                  '--triggerFontSize': '14px',
                  '--triggerPaddingInline': '12px',
                  '--triggerPaddingBlock': '6px',
                  '--triggerBorderRadius': '4px',
                  '--dropdownMaxWidth': 'auto',
                }}
                value={String(values[id] || '')}
                sort={false}
                errorMsg={errors[id] && touched[id] ? errors[id]?.toString() : undefined}
              />
            }
          />
        );

      case 'date':
        return (
          <ElementContainer
            label={
              <Box jc="fs">
                {label} {isRequired && getRequiredIcon()}
              </Box>
            }
            width="100%"
            isBold={false}
            justifyContent="flex-start"
            input={
              <DatepickerSimple
                variant="outline"
                name={id}
                value={String(values[id] || '')}
                onBlur={handleBlur}
                onChange={handleChange}
                error={errors[id] ? errors[id]?.toString() : null}
              />
            }
          />
        );
      default:
        throw new Error(`Invalid payment type: ${type}`);
    }
  }
  function getValidationSchema(
    ctx: PaymentUpdate,
    rows: PaymentUpdateRows[],
    initialValidationSchema?: { [key: string]: any },
  ): { [key: string]: any } {
    const validations = rows.reduce(
      (existingFields, row) =>
        row.id && row.container === 'field'
          ? {
              ...existingFields,
              [row.id]: row.validate ? row.validate(ctx) : undefined,
            }
          : { ...existingFields, ...getValidationSchema(ctx, row.paymentRows) },
      { ...initialValidationSchema },
    );

    return validations;
  }

  function getFinancialInstitutionIDByNumber(number: string) {
    for (const key in financialInstitutions) {
      if (financialInstitutions[key].FinancialInstitutionNumber.toString() === number) {
        return financialInstitutions[key].FinancialInstitutionID;
      }
    }
    return null; // Return null if the number is not found
  }
};

export default BankingUpdateMiniDialogue;
